import React, { useState, useRef, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { listCards } from '../../api/conceptCards';

const PHASE_LABELS = {
  uploading: 'Uploading',
  phase1: 'Vision',
  phase1_5: 'Embed',
  phase2: 'Objectives',
  phase2_5: 'Ranking',
  phase3: 'Tagging',
  phase4: 'Mindmap',
  phase5: 'Subtopics',
  phase6: 'Mapping',
};
const TOTAL_PHASES = 8;

// Sort keys by most recent uploadedAt descending, alphabetical fallback
function sortByNewest(keys, getDate) {
  return [...keys].sort((a, b) => {
    const dA = getDate(a);
    const dB = getDate(b);
    if (dA && dB) return new Date(dB) - new Date(dA);
    if (dA) return -1;
    if (dB) return 1;
    return a.localeCompare(b);
  });
}

function newestInExam(examData) {
  let newest = null;
  Object.values(examData.topics || {}).forEach(t => {
    if (t.uploadedAt && (!newest || new Date(t.uploadedAt) > new Date(newest)))
      newest = t.uploadedAt;
  });
  return newest;
}

/**
 * TreeDomain - Top-level domain node in the tree
 */
export const TreeDomain = ({
  domain,
  domainData,
  topicCount,
  isExpanded,
  onToggleExpand,
  isChecked,
  onCheck,
  showCheckboxes,
  selectedExams,
  selectedTopics,
  selectedTopic,
  onToggleExam,
  onToggleTopic,
  onTopicClick,
  onExamClick,
  expandedNodes,
  toggleExpand,
  onDeleteDeck,
  onMoveDomain,
  onDomainClick,
  onRenameDomain,
  onAddToExam,
  onOpenMergeDomain
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(domain);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleRenameConfirm = () => {
    const trimmed = editValue.trim();
    if (trimmed && trimmed !== domain && onRenameDomain) {
      onRenameDomain(domain, trimmed, domainData);
    }
    setIsEditing(false);
  };

  const handleRenameKeyDown = (e) => {
    if (e.key === 'Enter') handleRenameConfirm();
    if (e.key === 'Escape') { setEditValue(domain); setIsEditing(false); }
  };
  // Separate unlinked topics (shown flat) from real exams (shown as dropdowns)
  const unlinkedExam = domainData.exams?.['unlinked'];
  const unlinkedTopics = unlinkedExam
    ? sortByNewest(Object.keys(unlinkedExam.topics || {}), k => unlinkedExam.topics[k]?.uploadedAt)
    : [];
  // Sort exams by exam_date descending (most recent first), then by newest topic upload as fallback
  const realExamKeys = [...Object.keys(domainData.exams || {}).filter(k => k !== 'unlinked')].sort((a, b) => {
    const examA = domainData.exams[a];
    const examB = domainData.exams[b];
    const dateA = examA?.exam_date ? new Date(examA.exam_date) : null;
    const dateB = examB?.exam_date ? new Date(examB.exam_date) : null;
    if (dateA && dateB) return dateB - dateA;
    if (dateA) return -1;
    if (dateB) return 1;
    // Fallback: sort by newest topic upload
    const tA = newestInExam(examA);
    const tB = newestInExam(examB);
    if (tA && tB) return new Date(tB) - new Date(tA);
    if (tA) return -1;
    if (tB) return 1;
    return a.localeCompare(b);
  });

  const isNaplex = domain === 'NAPLEX Review';
  const isPtcb = domain === 'PTCB Review';

  // Exam review domains: flat clickable row, no chevron or children
  if (isNaplex || isPtcb) {
    const domainClass = isNaplex ? 'tree-domain--naplex' : 'tree-domain--ptcb';
    const headerClass = isNaplex ? 'tree-domain-header--naplex' : 'tree-domain-header--ptcb';
    return (
      <div className={`tree-domain ${domainClass}`}>
        <div
          className={`tree-item tree-domain-header ${headerClass}`}
          onClick={() => onDomainClick && onDomainClick(domain)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onDomainClick && onDomainClick(domain); } }}
        >
          <div className="tree-item-content">
            <span className="tree-label">{domain}</span>
          </div>
          <div className="tree-domain-right">
            {onOpenMergeDomain && (
              <span className="tree-domain-actions">
                <button
                  className="tree-action-btn tree-action-merge"
                  onClick={(e) => { e.stopPropagation(); onOpenMergeDomain(domain); }}
                  aria-label="Merge into another domain"
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M8 6h6a4 4 0 0 1 0 8H6" />
                    <path d="M16 18H10a4 4 0 0 1 0-8h8" />
                  </svg>
                </button>
              </span>
            )}
            <span className="tree-domain-arrow">&#8250;</span>
          </div>
        </div>
      </div>
    );
  }

  // Domain is clickable when multiselect is on (no checkboxes, but selection state drives it).
  const multiselectDomainClick = !showCheckboxes && onCheck ? onCheck : null;

  return (
    <div className="tree-domain">
      <div
        className={`tree-item tree-domain-header ${isChecked ? 'selected' : ''}`}
        onClick={multiselectDomainClick ? (e) => { if (!e.target.closest('button')) multiselectDomainClick(); } : undefined}
        style={multiselectDomainClick ? { cursor: 'pointer' } : undefined}
      >
        <div className="tree-item-content">
          {/* Chevron */}
          <button
            className={`tree-chevron ${isExpanded ? 'expanded' : ''}`}
            onClick={(e) => { e.stopPropagation(); onToggleExpand(); }}
            aria-label={isExpanded ? 'Collapse' : 'Expand'}
            aria-expanded={isExpanded}
          >
            ▶
          </button>

          {/* Checkbox (optional) */}
          {showCheckboxes && (
            <input
              type="checkbox"
              className="tree-checkbox"
              checked={isChecked}
              onChange={onCheck}
              aria-label={`Select ${domain}`}
            />
          )}

          {/* Label — inline edit or static */}
          {isEditing ? (
            <input
              ref={inputRef}
              className="tree-domain-rename-input"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={handleRenameKeyDown}
              onBlur={handleRenameConfirm}
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <span className="tree-label">{domain}</span>
          )}
        </div>

        <div className="tree-domain-right">
          <span className="tree-domain-actions">
            {onOpenMergeDomain && !isEditing && (
              <button
                className="tree-action-btn tree-action-merge"
                onClick={(e) => { e.stopPropagation(); onOpenMergeDomain(domain); }}
                aria-label="Merge into another domain"
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M8 6h6a4 4 0 0 1 0 8H6" />
                  <path d="M16 18H10a4 4 0 0 1 0-8h8" />
                </svg>
              </button>
            )}
            {onRenameDomain && !isEditing && (
              <button
                className="tree-action-btn tree-domain-edit-btn"
                onClick={(e) => { e.stopPropagation(); setEditValue(domain); setIsEditing(true); }}
                aria-label="Rename domain"
              >
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
              </button>
            )}
          </span>
          {/* Count badge */}
          <span className="tree-count">{topicCount}</span>
        </div>
      </div>

      {/* Children */}
      <div className={isExpanded ? 'tree-children' : 'tree-children tree-collapsed'}>
        {/* Unlinked topics shown flat (no exam dropdown) */}
        {unlinkedTopics.map(topic => {
          const topicData = unlinkedExam.topics[topic];
          const topicKey = `${domain}::unlinked::${topic}`;
          const isTopicSelected = selectedTopics.has(topicKey);

          return (
            <TreeTopic
              key={topic}
              domain={domain}
              examId="unlinked"
              topic={topic}
              topicData={topicData}
              isChecked={isTopicSelected}
              onCheck={() => onToggleTopic(domain, 'unlinked', topic)}
              showCheckboxes={showCheckboxes}
              isDisabled={isChecked}
              selectedTopic={selectedTopic}
              selectedTopics={selectedTopics}
              onTopicClick={onTopicClick}
              onDeleteDeck={onDeleteDeck}
              onMoveDomain={onMoveDomain}
              onAddToExam={onAddToExam}
            />
          );
        })}

        {/* Real exams shown as collapsible groups (including past exams) */}
        {realExamKeys.map(examKey => {
          const examData = domainData.exams[examKey];
          const examNodeKey = `${domain}::${examKey}`;
          const isExamExpanded = expandedNodes.has(examNodeKey);
          const isExamSelected = selectedExams.has(examNodeKey);

          return (
            <TreeExam
              key={examKey}
              domain={domain}
              examId={examKey}
              examData={examData}
              isExpanded={isExamExpanded}
              onToggleExpand={() => toggleExpand(examNodeKey)}
              isChecked={isExamSelected}
              onCheck={() => onToggleExam(domain, examKey)}
              showCheckboxes={showCheckboxes}
              isDomainSelected={isChecked}
              selectedTopics={selectedTopics}
              selectedTopic={selectedTopic}
              onToggleTopic={onToggleTopic}
              onTopicClick={onTopicClick}
              onExamClick={onExamClick}
              onDeleteDeck={onDeleteDeck}
              onMoveDomain={onMoveDomain}
            />
          );
        })}
      </div>
    </div>
  );
};

/**
 * TreeExam - Exam node in the tree
 */
export const TreeExam = ({
  domain,
  examId,
  examData,
  isExpanded,
  onToggleExpand,
  isChecked,
  onCheck,
  showCheckboxes,
  isDomainSelected,
  selectedTopics,
  selectedTopic,
  onToggleTopic,
  onTopicClick,
  onExamClick,
  onDeleteDeck,
  onMoveDomain
}) => {
  const topics = sortByNewest(Object.keys(examData.topics || {}), k => examData.topics[k]?.uploadedAt);

  const isExamNavigationSelected = selectedTopic === `__exam__::${domain}::${examId}`;

  const handleExamLabelClick = () => {
    if (onExamClick) {
      onExamClick(domain, examId);
    }
  };

  return (
    <div className="tree-exam">
      <div
        className={`tree-item tree-exam-header ${isExamNavigationSelected || isChecked ? 'selected' : ''}`}
        onClick={handleExamLabelClick}
        style={{ cursor: onExamClick ? 'pointer' : 'default' }}
      >
        <div className="tree-item-content">
          {/* Chevron */}
          <button
            className={`tree-chevron ${isExpanded ? 'expanded' : ''}`}
            onClick={(e) => { e.stopPropagation(); onToggleExpand(); }}
            aria-label={isExpanded ? 'Collapse' : 'Expand'}
            aria-expanded={isExpanded}
          >
            ▶
          </button>

          {/* Checkbox (optional, disabled if domain selected) */}
          {showCheckboxes && (
            <input
              type="checkbox"
              className="tree-checkbox"
              checked={isChecked || isDomainSelected}
              disabled={isDomainSelected}
              onChange={onCheck}
              onClick={(e) => e.stopPropagation()}
              aria-label={`Select ${examData.title}`}
            />
          )}

          {/* Label */}
          <span className="tree-label tree-exam-label">{examData.title}</span>
        </div>

        {/* Count badge — drugCount when available, otherwise topicCount */}
        <span className="tree-count">
          {examData.drugCount != null ? examData.drugCount : examData.topicCount}
        </span>
      </div>

      {/* Children */}
      <div className={isExpanded ? 'tree-exam-children' : 'tree-exam-children tree-collapsed'}>
        {topics.map(topic => {
          const topicData = examData.topics[topic];
          const topicKey = `${domain}::${examId}::${topic}`;
          const isTopicSelected = selectedTopics.has(topicKey);

          return (
            <TreeTopic
              key={topic}
              domain={domain}
              examId={examId}
              topic={topic}
              topicData={topicData}
              isChecked={isTopicSelected}
              onCheck={() => onToggleTopic(domain, examId, topic)}
              showCheckboxes={showCheckboxes}
              isDisabled={isDomainSelected || isChecked}
              selectedTopic={selectedTopic}
              selectedTopics={selectedTopics}
              onTopicClick={onTopicClick}
              onDeleteDeck={onDeleteDeck}
              onMoveDomain={onMoveDomain}
            />
          );
        })}
      </div>
    </div>
  );
};

/**
 * TreeTopic - Topic node (leaf) in the tree
 */
export const TreeTopic = ({
  domain,
  examId,
  topic,
  topicData,
  isChecked,
  onCheck,
  showCheckboxes,
  isDisabled,
  selectedTopic,
  selectedTopics = new Set(),
  onTopicClick,
  onDeleteDeck,
  onMoveDomain,
  onAddToExam
}) => {
  const isNotes = topicData.isNotes;
  const processingDeck = topicData.decks?.find(d =>
    d.parsing_status === 'parsing' || d.parsing_status === 'pending' || d.parsing_status === 'uploading'
  );

  const isProcessing = !!processingDeck;

  // Concept-card forging signal: shared cross-tree query, deduped by react-query.
  // When the topic has at least one deck with pending_placeholders > 0,
  // render the same tree-processing chrome in the blue --forging variant.
  const forgingQuery = useQuery({
    queryKey: ['tree-pending-concept-cards'],
    queryFn: async () => {
      const res = await listCards({ include_placeholders: true });
      const list = Array.isArray(res?.data?.pending_placeholders)
        ? res.data.pending_placeholders
        : [];
      const counts = {};
      for (const item of list) {
        const key = String(item?.deck_id || item?.deck_public_id || '');
        if (!key) continue;
        counts[key] = (counts[key] || 0) + 1;
      }
      return counts;
    },
    refetchInterval: (queryOrData) => {
      const data = queryOrData?.state?.data || queryOrData;
      return data && Object.keys(data).length > 0 ? 8000 : 30000;
    },
    refetchOnWindowFocus: true,
    staleTime: 4000,
  });
  const forgingCounts = forgingQuery.data || {};
  const forgingDecks = (topicData.decks || [])
    .map((d) => ({ deck: d, count: forgingCounts[String(d?.id || d?._id || '')] || 0 }))
    .filter((entry) => entry.count > 0);

  // Extract progress from real pipeline
  const pipelineCfg = processingDeck?.pipeline_config;
  const phasesCompleted = pipelineCfg?.phases_completed || [];
  const currentPhase = pipelineCfg?.current_phase || null;
  const total = pipelineCfg?.progress?.total_phases || TOTAL_PHASES;
  const progressPct = (phasesCompleted.length / total) * 100;
  const isCloudParse = processingDeck?.parse_mode === 'cloud';
  const isUploading = processingDeck?.parsing_status === 'uploading';
  const uploadProgress = processingDeck?.upload_progress;
  const rawLabel = isNotes && isProcessing
    ? 'Notes'
    : isUploading
      ? (uploadProgress ? `Uploading ${uploadProgress.current}/${uploadProgress.total}` : 'Uploading')
      : currentPhase ? PHASE_LABELS[currentPhase] : (phasesCompleted.length > 0 ? 'Finishing' : null);
  const phaseLabel = rawLabel && isCloudParse && !isUploading ? `☁ ${rawLabel}` : rawLabel;
  // Visual style: uploading > cloud > default. Forging is rendered as
  // its own sub-row underneath the deck (see end of TreeTopic), not
  // inlined into the topic header.
  const styleVariant = isUploading ? 'uploading' : isCloudParse ? 'cloud' : '';

  const handleLabelClick = () => {
    if (onTopicClick) {
      onTopicClick(domain, examId, topic, topicData);
    }
  };

  const isNavigationSelected = selectedTopic
    ? selectedTopic === `${domain}::${topic}`
    : selectedTopics.has(topicData.key);

  const isSelected = showCheckboxes ? isChecked : isNavigationSelected;
  const canAddToExam = onAddToExam && examId === 'unlinked' && !isProcessing;
  const hasActions = onDeleteDeck || onMoveDomain || canAddToExam;

  return (
    <div className="tree-topic">
      <div
        className={`tree-item tree-topic-header ${isSelected ? 'selected' : ''} ${isProcessing ? 'tree-processing' : ''}`}
        onClick={handleLabelClick}
        style={{ cursor: onTopicClick ? 'pointer' : 'default' }}
      >
        <div className="tree-item-content">
          {showCheckboxes && (
            <input
              type="checkbox"
              className="tree-checkbox"
              checked={isChecked || isDisabled}
              disabled={isDisabled}
              onChange={onCheck}
              onClick={(e) => e.stopPropagation()}
              aria-label={`Select ${topic}`}
            />
          )}
          {isProcessing && <span className={`tree-processing-dot${styleVariant ? ` tree-processing-dot--${styleVariant}` : ''}`} />}
          <span className="tree-label">{topic}</span>
        </div>

        <div className="tree-topic-right">
          {hasActions && !isProcessing && (
            <span className="tree-topic-actions">
              {canAddToExam && (
                <button
                  className="tree-action-btn tree-action-add"
                  onClick={(e) => { e.stopPropagation(); onAddToExam(domain, examId, topic, topicData); }}
                  aria-label="Add to exam"
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                    <line x1="12" y1="5" x2="12" y2="19"></line>
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                  </svg>
                </button>
              )}
              {onMoveDomain && (
                <button
                  className="tree-action-btn"
                  onClick={(e) => { e.stopPropagation(); onMoveDomain(domain, examId, topic, topicData); }}
                  aria-label="Move to domain"
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                    <polyline points="17 8 12 3 7 8"></polyline>
                    <line x1="12" y1="3" x2="12" y2="15"></line>
                  </svg>
                </button>
              )}
              {onDeleteDeck && (
                <button
                  className="tree-action-btn tree-action-delete"
                  onClick={(e) => { e.stopPropagation(); onDeleteDeck(domain, examId, topic, topicData); }}
                  aria-label="Delete deck"
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="3 6 5 6 21 6"></polyline>
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                  </svg>
                </button>
              )}
            </span>
          )}
          {isProcessing ? (
            phaseLabel && <span className={`tree-phase-hint${styleVariant ? ` tree-phase-hint--${styleVariant}` : ''}`}>{phaseLabel}</span>
          ) : isNotes ? (
            <span className="tree-count-group">
              <span className="tree-count tree-count-notes">Notes</span>
              {topicData.notesCardCount != null && topicData.notesCardCount > 0 && (
                <span className="tree-count">{topicData.notesCardCount}</span>
              )}
            </span>
          ) : (() => {
            const objs = topicData.objectives || [];
            const realCount = objs.filter(o => !o.synthetic && !o.manual).length;
            const synthCount = objs.filter(o => o.synthetic).length;
            const manualCount = objs.filter(o => o.manual).length;
            return (
              <span className="tree-count-group">
                {realCount > 0 && <span className="tree-count">{realCount}</span>}
                {synthCount > 0 && <span className="tree-count tree-count-synthetic">{synthCount}</span>}
                {manualCount > 0 && <span className="tree-count tree-count-manual">{manualCount}</span>}
                {realCount === 0 && synthCount === 0 && manualCount === 0 && <span className="tree-count">0</span>}
              </span>
            );
          })()}
        </div>
      </div>

      {isProcessing && (
        <div className={`tree-processing-track${styleVariant ? ` tree-processing-track--${styleVariant}` : ''}`}>
          <div className={`tree-processing-fill${styleVariant ? ` tree-processing-fill--${styleVariant}` : ''}`} style={{ width: `${Math.max(isUploading && uploadProgress ? (uploadProgress.current / uploadProgress.total) * 100 : progressPct, 2)}%` }} />
        </div>
      )}
      {forgingDecks.map(({ deck, count }) => {
        const deckTitle = String(deck?.title || deck?.filename || topic || '').replace(/\.(pptx?|pdf)$/i, '') || topic;
        return (
          <div
            key={`forge-${deck?.id || deck?._id || deckTitle}`}
            className="tree-item tree-processing tree-forging-row"
          >
            <div className="tree-item-content">
              <span className="tree-processing-dot tree-processing-dot--forging" />
              <span className="tree-label">Forging concept cards</span>
            </div>
            <div className="tree-topic-right">
              <span className="tree-phase-hint tree-phase-hint--forging">{`· ${count}`}</span>
            </div>
            <div className="tree-processing-track tree-processing-track--forging">
              <div className="tree-processing-fill tree-processing-fill--forging" style={{ width: '100%' }} />
            </div>
          </div>
        );
      })}
    </div>
  );
};
